import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslate } from '@/translations';
import {
  Alert,
  Checkbox,
  ColorPicker,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Spin,
  Switch,
  Tooltip,
  TreeSelect,
  UploadFile,
} from 'antd';
import dayjs from 'dayjs';
import {
  AppDataRuleParams,
  AppFeature,
  Customer,
  CustomerHierarchy,
  CustomerRuleModel,
  DataRule,
  DataRuleParamDataType,
} from '@/types';
import { DragUpload, faInfoCircle, Icon } from '@/ui/components';
import {
  acceptImageFiles,
  FORMAT_DATE_WITH_TIME_DAYJS,
  MAX_FILE_SIZE,
} from '@/utils/constants';
import { getFormattedLabel } from '@/utils/helpers';
import clonedeep from 'lodash.clonedeep';

const DATA_RULE_DATETIME_STORAGE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

type CustomerPopupProps = {
  data?: Customer;
  hierarchyData?: CustomerHierarchy[];
  appFeatures?: AppFeature[];
  appDataRules?: DataRule[];
  onSubmit(user: Customer): Promise<string | undefined>;
  onCancel(): void;
};

export const CustomerPopup: FC<CustomerPopupProps> = ({
  data,
  hierarchyData,
  appFeatures,
  appDataRules,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm<Customer>();

  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [rules, setRules] = useState<CustomerRuleModel[]>([]);
  const parentCustomerId = Form.useWatch('parentCustomerId', form);
  const isParentCustomer = !parentCustomerId;

  const isAdd = data && !data.customerId;

  const hierarchyFilter = (
    customerId: number | undefined,
    hierarchy: CustomerHierarchy[],
  ) => {
    const list = hierarchy.filter((o) => o.customerId !== customerId);
    list.forEach((o) => {
      o.children = hierarchyFilter(customerId, o.children);
    });
    return list;
  };

  const customersHierarchyData = hierarchyData?.length
    ? hierarchyFilter(data?.customerId, clonedeep(hierarchyData))
    : [];

  useEffect(() => {
    setFile(data?.iconUrl);
    setRules(data?.rules || []);
    setErrorMessage(undefined);
    form.resetFields();
    if (data) form.setFieldsValue(data);
  }, [data, form]);

  const handleValueChange = (files: UploadFile[]) => {
    if (files.length === 0) {
      setFile(undefined);
      return;
    }

    const item = files[0].originFileObj as File;
    if (!item) return;

    const reader = new FileReader();
    reader.onload = ({ target }) => {
      if (target?.result) setFile(target.result as string);
    };
    reader.readAsDataURL(item); // convert to base64 string
  };

  const onChangeRuleEnable = (ruleId: number, checked: boolean) => {
    const copyRules = [...rules];
    const ruleIdx = copyRules.findIndex((o) => o.ruleId === ruleId);
    if (ruleIdx > -1) {
      copyRules[ruleIdx].isEnabled = checked;
    } else {
      copyRules.push({ ruleId, isEnabled: checked, values: [] });
    }
    setRules(copyRules);
  };

  const onChangeParamValue = (
    ruleId: number,
    paramId: number,
    value: string,
  ) => {
    const copyRules = [...rules];
    const ruleIdx = copyRules.findIndex((o) => o.ruleId === ruleId);
    if (ruleIdx > -1) {
      const paramIdx = copyRules[ruleIdx].values.findIndex(
        (v) => v.paramId === paramId,
      );
      if (paramIdx > -1) {
        copyRules[ruleIdx].values[paramIdx].paramValue = value;
      } else {
        copyRules[ruleIdx].values.push({ paramId, paramValue: value });
      }
    } else {
      copyRules.push({
        ruleId,
        isEnabled: false,
        values: [{ paramId, paramValue: value }],
      });
    }
    setRules(copyRules);
  };

  const stripSubCustomerCutoff = useCallback(
    (customerRules: CustomerRuleModel[]) => {
      if (!isParentCustomer) return customerRules;
      return customerRules.map((rule) => {
        const catalogRule = appDataRules?.find((r) => r.id === rule.ruleId);
        const subParamId = catalogRule?.parameters.find(
          (p) => p.paramKey === AppDataRuleParams.CutoffDateTimeSubCustomer,
        )?.id;
        if (!subParamId) return rule;
        return {
          ...rule,
          values: (rule.values || []).filter((v) => v.paramId !== subParamId),
        };
      });
    },
    [isParentCustomer, appDataRules],
  );

  const handleSubmit = useCallback(
    async (customer: Customer) => {
      customer.iconUrl = file;
      customer.rules = stripSubCustomerCutoff(rules);
      setErrorMessage(undefined);
      setLoading(true);
      const error = await onSubmit(customer);
      setErrorMessage(error);

      setLoading(false);
      if (!error) onCancel();
    },
    [file, stripSubCustomerCutoff, rules, onSubmit, onCancel],
  );

  return (
    <Modal
      open={!!data}
      onCancel={onCancel}
      title={`${t(isAdd ? 'create' : 'edit')} ${t('customer')}`}
      maskClosable
      closable={false}
      okText={t('save')}
      cancelText={t('cancel')}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={680}
      className="mw-100"
      forceRender
    >
      <Spin spinning={loading}>
        <Form
          id="customer-form"
          form={form}
          onFinish={handleSubmit}
          size="large"
          labelCol={{ span: 6 }}
          autoComplete="off"
        >
          <Form.Item<Customer> name="customerId" hidden>
            <Input />
          </Form.Item>

          <Form.Item<Customer>
            label={t('customerCode')}
            name="customerCode"
            required
            rules={[{ required: true }]}
          >
            <Input disabled={!isAdd} maxLength={10} />
          </Form.Item>

          <Form.Item<Customer>
            label={t('customerName')}
            name="customerName"
            required
            rules={[{ required: true }]}
          >
            <Input maxLength={100} />
          </Form.Item>

          <Form.Item<Customer>
            label={t('parentCustomer')}
            name="parentCustomerId"
          >
            <TreeSelect
              showSearch
              treeData={customersHierarchyData}
              fieldNames={{ label: 'customerName', value: 'customerId' }}
              filterTreeNode={(search, item) =>
                (item.customerName || '')
                  .toLowerCase()
                  .indexOf(search.toLowerCase()) >= 0
              }
              allowClear
            />
          </Form.Item>

          <Form.Item<Customer> label={t('features')} name="features">
            <TreeSelect
              showSearch
              multiple
              treeCheckable={true}
              showCheckedStrategy="SHOW_PARENT"
              treeData={[
                {
                  code: 'All',
                  description: 'All',
                  children: appFeatures,
                },
              ]}
              fieldNames={{ label: 'description', value: 'code' }}
              filterTreeNode={(search, item) =>
                (item.description || '')
                  .toLowerCase()
                  .indexOf(search.toLowerCase()) >= 0
              }
              allowClear
            />
          </Form.Item>

          <Form.Item<Customer> label={t('rules')}>
            {appDataRules?.map((rule, idx) => {
              const item = rules?.find((r) => r.ruleId === rule.id);
              return (
                <>
                  {idx > 0 ? <Divider className="my-3" /> : null}
                  <div
                    className="d-flex align-items-center"
                    style={{ height: 40 }}
                  >
                    <Checkbox
                      checked={item?.isEnabled}
                      onChange={(e) =>
                        onChangeRuleEnable(rule.id, e.target.checked)
                      }
                    />
                    <b className="fs-6 mx-2">
                      {getFormattedLabel(rule.ruleKey)}
                    </b>
                    <Tooltip title={rule.description}>
                      <Icon icon={faInfoCircle} className="me-1" />
                    </Tooltip>
                  </div>
                  {item?.isEnabled && rule.parameters.length ? (
                    <div className="d-flex align-items-center mt-2">
                      <div className="text-end pe-2" style={{ width: 100 }}>
                        Configuration:
                      </div>
                      <table className="flex-fill">
                        {rule.parameters
                          .filter(
                            (param) =>
                              !(
                                isParentCustomer &&
                                param.paramKey ===
                                  AppDataRuleParams.CutoffDateTimeSubCustomer
                              ),
                          )
                          .map((param) => {
                            const paramValue = item?.values?.find(
                              (v) => v.paramId === param.id,
                            )?.paramValue;
                            return (
                              <tr>
                                <td>{param.paramName}:</td>
                                <td>
                                  {param.dataType ===
                                  DataRuleParamDataType.String ? (
                                    <Input
                                      value={paramValue}
                                      onChange={(e) =>
                                        onChangeParamValue(
                                          rule.id,
                                          param.id,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  ) : param.dataType ===
                                    DataRuleParamDataType.Number ? (
                                    <InputNumber
                                      value={paramValue}
                                      onChange={(e) =>
                                        onChangeParamValue(
                                          rule.id,
                                          param.id,
                                          e || '',
                                        )
                                      }
                                    />
                                  ) : param.dataType ===
                                    DataRuleParamDataType.Boolean ? (
                                    <Checkbox
                                      checked={
                                        paramValue === 'true' ? true : false
                                      }
                                      onChange={(e) =>
                                        onChangeParamValue(
                                          rule.id,
                                          param.id,
                                          e.target.checked ? 'true' : 'false',
                                        )
                                      }
                                    />
                                  ) : param.dataType ===
                                    DataRuleParamDataType.DateTime ? (
                                    <DatePicker
                                      showTime
                                      value={
                                        paramValue &&
                                        dayjs(paramValue).isValid()
                                          ? dayjs(paramValue)
                                          : null
                                      }
                                      format={FORMAT_DATE_WITH_TIME_DAYJS}
                                      onChange={(date) =>
                                        onChangeParamValue(
                                          rule.id,
                                          param.id,
                                          date
                                            ? date.format(
                                                DATA_RULE_DATETIME_STORAGE_FORMAT,
                                              )
                                            : '',
                                        )
                                      }
                                    />
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                      </table>
                    </div>
                  ) : null}
                </>
              );
            })}
          </Form.Item>

          <Form.Item<Customer>
            name="color1"
            label={t('color1')}
            getValueFromEvent={(color) => `#${color.toHex()}`}
          >
            <ColorPicker />
          </Form.Item>

          <Form.Item<Customer>
            name="color2"
            label={t('color2')}
            getValueFromEvent={(color) => `#${color.toHex()}`}
          >
            <ColorPicker />
          </Form.Item>

          <Form.Item<Customer> label={t('icon')} name="iconUrl">
            <DragUpload
              accept={acceptImageFiles}
              uploadButtonText={t('upload')}
              maxFileSize={MAX_FILE_SIZE}
              showUploadBtn={false}
              onFileChanged={handleValueChange}
            />
          </Form.Item>

          <Form.Item<Customer>
            name="isActive"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item<Customer>
            name="productImageryGenerationEnabled"
            label={t('imagery.customerGeneration')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {errorMessage ? (
            <Alert message={errorMessage} type="error" className="mb-4" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
